import React, { useState, useEffect, useRef, useMemo } from 'react';
import LearningStatusBadge, { getLearningStatusMeta } from '../components/ui/LearningStatusBadge';
import OptimizedImage from '../components/ui/OptimizedImage';
import { LeafIcon, IconList, IconSearch, IconFolder, IconChart } from '../components/ui/Icons';
import ExportModal from '../components/common/ExportModal';
import { CheckSquare, Square, X, BookOpen, Briefcase, Download } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';

const getLearningStatusLabel = (status) => getLearningStatusMeta(status).label;

const Bestiary = ({ plants, categories, userPlantData, onPlantClick, onCompareClick, comparePlants, trackInteraction, onUpdateUserPlantData, selectedPlantList, setSelectedPlantList, listStats, listLoading, fetchWithAuth, user }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [sortBy, setSortBy] = useState('botanical_name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [sizeLevel, setSizeLevel] = useState(3);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportMode, setExportMode] = useState('filtered'); // filtered | selected
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBulking, setIsBulking] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const { showToast: addToast } = useToast();
    const navigate = useNavigate();

    // --- Batch Refresh Images (Admin only) ---
    const handleBatchRefreshImages = async () => {
        if (!user?.is_staff || isRefreshing) return;
        
        const confirmMsg = "Möchtest du für alle Pflanzen in dieser Liste, die noch keine Bilder haben, eine Wikipedia-Suche starten? Dies kann einen Moment dauern.";
        if (!window.confirm(confirmMsg)) return;

        setIsRefreshing(true);
        try {
            // Use currently filtered/sorted plants from useMemo (sortedPlants below)
            const plantIds = sortedPlants.map(p => p.id);
            const res = await fetchWithAuth('/api/plants/batch_refresh_images/', {
                method: 'POST',
                body: JSON.stringify({ plant_ids: plantIds })
            });

            if (res.ok) {
                const data = await res.json();
                addToast(`${data.refreshed_count} Bilder erfolgreich aktualisiert.`, 'success');
                // Optional: trigger a re-fetch of plants if needed, but Bestiary usually gets them from parent
            } else {
                addToast('Fehler beim Aktualisieren der Bilder.', 'error');
            }
        } catch (error) {
            console.error("Refresh images error:", error);
            addToast('Fehler beim Aktualisieren der Bilder.', 'error');
        } finally {
            setIsRefreshing(false);
        }
    };

    // --- Suggest Learned Plants ---
    const [suggestedPlants, setSuggestedPlants] = useState([]);
    const [showSuggestBanner, setShowSuggestBanner] = useState(false);
    const [markingLearned, setMarkingLearned] = useState(false);

    useEffect(() => {
        if (!fetchWithAuth || !plants || plants.length === 0) return;
        // Check for learned suggestions after a short delay (non-blocking)
        const timer = setTimeout(async () => {
            try {
                const res = await fetchWithAuth('/api/plants/suggest-learned/');
                if (res.ok) {
                    const data = await res.json();
                    if (data.suggested_plants && data.suggested_plants.length > 0) {
                        setSuggestedPlants(data.suggested_plants);
                        setShowSuggestBanner(true);
                    }
                }
            } catch (e) {
                // Silently ignore — this is a non-critical feature
            }
        }, 5000);
        return () => clearTimeout(timer);
    }, [plants]); // Only run once when plants first load

    const handleMarkAllLearned = async () => {
        if (!fetchWithAuth || markingLearned) return;
        setMarkingLearned(true);
        try {
            const plantIds = suggestedPlants.map(p => p.id);
            const res = await fetchWithAuth('/api/plants/mark-learned/', {
                method: 'POST',
                body: JSON.stringify({ plant_ids: plantIds }),
            });
            if (res.ok) {
                // Update local userPlantData for each plant
                plantIds.forEach(id => {
                    if (onUpdateUserPlantData) {
                        onUpdateUserPlantData(id, { learning_status: 'LEARNED' });
                    }
                });
                setShowSuggestBanner(false);
                setSuggestedPlants([]);
            }
        } catch (e) {
            console.error('Fehler beim Markieren als gelernt:', e);
        } finally {
            setMarkingLearned(false);
        }
    };

    // Track Bestiary-View
    // Track Bestiary-View nur einmal beim ersten Laden
    const hasTrackedBestiaryView = useRef(false);
    useEffect(() => {
        if (!hasTrackedBestiaryView.current && trackInteraction && plants) {
            hasTrackedBestiaryView.current = true;
            trackInteraction('BESTIARY_VIEW', {
                total_plants: plants.length,
                learned_count: Object.values(userPlantData).filter(u => u.learning_status === 'LEARNED').length
            });
        }
    }, [plants, userPlantData, trackInteraction]); // Nur einmal beim Mount

    const toggleSelect = (e, id) => {
        e.stopPropagation();
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleBulkCreateJournalEntries = async () => {
        if (selectedIds.length === 0 || isBulking) return;
        setIsBulking(true);
        try {
            const response = await fetchWithAuth('/api/apprentice/pflanze-der-woche/bulk_create_from_plants/', {
                method: 'POST',
                body: JSON.stringify({ plant_ids: selectedIds })
            });

            if (response.ok) {
                const data = await response.json();
                addToast(data.message || 'Pflanzen erfolgreich erstellt.', 'success');
                setSelectedIds([]);
                // Navigate to the list to see the results
                navigate('/apprentice/pflanze-der-woche');
            } else {
                const errorData = await response.json().catch(() => ({}));
                addToast(errorData.error || 'Fehler bei der Bulk-Erstellung.', 'error');
            }
        } catch (error) {
            console.error('Bulk creation error:', error);

            addToast('Netzwerkfehler beim Bulk-Erstellen.', 'error');
        } finally {
            setIsBulking(false);
        }
    };

    const filteredPlants = plants.filter(plant => {
        // 0. Filter by List
        if (selectedPlantList && (!plant.plant_lists || !plant.plant_lists.includes(selectedPlantList))) {
            return false;
        }

        const matchesSearch = !searchTerm ||
            plant.botanical_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (plant.german_name && plant.german_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (plant.family && plant.family.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesCategory = !filterCategory || String(plant.category) === filterCategory;

        const plantData = userPlantData[plant.id] || { learning_status: 'NOT_LEARNED' };
        const matchesStatus = !filterStatus || plantData.learning_status === filterStatus;

        return matchesSearch && matchesCategory && matchesStatus;
    });

    // Derive available categories from plants in the current list
    const listPlants = useMemo(() => {
        return selectedPlantList
            ? plants.filter(p => p.plant_lists && p.plant_lists.includes(selectedPlantList))
            : plants;
    }, [plants, selectedPlantList]);

    const availableCategories = useMemo(() => {
        const categoryIds = new Set(listPlants.map(p => p.category));
        return categories.filter(c => categoryIds.has(c.id));
    }, [listPlants, categories]);

    const sortedPlants = [...filteredPlants].sort((a, b) => {
        let aVal, bVal;
        switch (sortBy) {
            case 'botanical_name':
                aVal = a.botanical_name.toLowerCase();
                bVal = b.botanical_name.toLowerCase();
                break;
            case 'german_name':
                aVal = (a.german_name || '').toLowerCase();
                bVal = (b.german_name || '').toLowerCase();
                break;
            case 'family':
                aVal = (a.family || '').toLowerCase();
                bVal = (b.family || '').toLowerCase();
                break;
            case 'status':
                const aData = userPlantData[a.id] || { learning_status: 'NOT_LEARNED' };
                const bData = userPlantData[b.id] || { learning_status: 'NOT_LEARNED' };
                const statusOrder = { 'LEARNED': 3, 'IN_PROGRESS': 2, 'NOT_LEARNED': 1 };
                aVal = statusOrder[aData.learning_status] || 0;
                bVal = statusOrder[bData.learning_status] || 0;
                break;
            default:
                return 0;
        }
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection('asc');
        }
    };

    const normalizedComparePlants = comparePlants || [];
    const isInCompare = (plantId) => normalizedComparePlants.some(p => p.id === plantId);
    const canAddToCompare = normalizedComparePlants.length < 2;

    const stats = useMemo(() => {
        return {
            total: listPlants.length,
            learned: listPlants.filter(p => userPlantData[p.id]?.learning_status === 'LEARNED').length,
            inProgress: listPlants.filter(p => userPlantData[p.id]?.learning_status === 'IN_PROGRESS').length,
            notLearned: listPlants.filter(p => !userPlantData[p.id] || userPlantData[p.id].learning_status === 'NOT_LEARNED').length
        };
    }, [listPlants, userPlantData]);

    return (
        <div className="p-4 sm:p-6 md:p-8 lg:p-10 max-w-7xl mx-auto w-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-3">
                        <span className="text-3xl sm:text-4xl">📚</span>
                        <span>Pflanzen-Bestiary</span>
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Entdecke alle Pflanzen, verfolge deinen Fortschritt und vergleiche sie miteinander.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {user?.is_staff && (
                        <button
                            onClick={handleBatchRefreshImages}
                            disabled={isRefreshing}
                            className={`flex items-center gap-2 px-4 py-2 bg-plantlix-600 hover:bg-plantlix-700 text-white rounded-xl font-bold shadow-sm hover:shadow-md transition-all disabled:opacity-50 ${isRefreshing ? 'animate-pulse' : ''}`}
                            title="Fehlende Bilder via Wikipedia ergänzen"
                        >
                            <LeafIcon className="w-4 h-4" />
                            <span className="hidden sm:inline">Admin: Bilder aktualisieren</span>
                        </button>
                    )}
                    <button
                        onClick={() => {
                            setExportMode('filtered');
                            setIsExportModalOpen(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1a1a1a] text-plantlix-500 border-2 border-plantlix-500 rounded-xl font-bold hover:bg-plantlix-500 hover:text-white shadow-sm hover:shadow-md transition-all group/export"
                    >
                        <Download className="w-4 h-4 transition-transform group-hover/export:-translate-y-1" />
                        <span className="hidden sm:inline">Liste exportieren ({sortedPlants.length})</span>
                    </button>
                </div>
            </div>

            {/* 🌿 Suggest Learned Banner */}
            {showSuggestBanner && suggestedPlants.length > 0 && (
                <div className="mb-6 bg-gradient-to-r from-plantlix-50 to-plantlix-50 dark:from-plantlix-900/30 dark:to-plantlix-900/20 border-2 border-plantlix-300 dark:border-plantlix-700 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fadeIn shadow-sm">
                    <div className="flex items-start gap-3">
                        <span className="text-3xl flex-shrink-0">🌿</span>
                        <div>
                            <p className="font-bold text-plantlix-900 dark:text-plantlix-100 text-base">
                                {suggestedPlants.length === 1
                                    ? 'Du hast eine Pflanze intensiv kennengelernt!'
                                    : `Du hast ${suggestedPlants.length} Pflanzen intensiv kennengelernt!`}
                            </p>
                            <p className="text-sm text-plantlix-600 dark:text-plantlix-300 mt-0.5">
                                {suggestedPlants.map(p => p.botanical_name || p.german_name).join(', ')}
                            </p>
                            <p className="text-xs text-plantlix-500 dark:text-plantlix-400 mt-1">
                                Möchtest du {suggestedPlants.length === 1 ? 'sie' : 'diese'} als <strong>Gelernt</strong> markieren?
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={handleMarkAllLearned}
                            disabled={markingLearned}
                            className="px-4 py-2 bg-plantlix-500 hover:bg-plantlix-600 disabled:bg-plantlix-400 text-white rounded-lg font-semibold text-sm transition-all shadow-sm flex items-center gap-2"
                        >
                            {markingLearned ? (
                                <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span> Wird gespeichert...</>
                            ) : (
                                <>✅ Ja, als gelernt markieren</>
                            )}
                        </button>
                        <button
                            onClick={() => setShowSuggestBanner(false)}
                            className="px-3 py-2 text-plantlix-600 dark:text-plantlix-300 hover:bg-plantlix-100 dark:hover:bg-plantlix-900/40 rounded-lg text-sm transition-colors"
                        >
                            Später
                        </button>
                    </div>
                </div>
            )}

            {/* Statistiken */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <div
                    onClick={() => setFilterStatus('')}
                    className={`card card-elevated glass bg-white dark:bg-[#1a1a1a] rounded-xl p-3 sm:p-5 border transition-all cursor-pointer ${filterStatus === ''
                        ? 'border-plantlix-400 dark:border-plantlix-600 ring-2 ring-plantlix-200 dark:ring-plantlix-800'
                        : 'border-gray-200 dark:border-gray-800 hover:border-plantlix-300 dark:hover:border-plantlix-700'
                        }`}
                >
                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">{stats.total}</div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Gesamt</div>
                </div>
                <div
                    onClick={() => setFilterStatus(filterStatus === 'LEARNED' ? '' : 'LEARNED')}
                    className={`card card-elevated glass bg-gradient-to-br from-plantlix-50 to-plantlix-100 dark:from-plantlix-900/30 dark:to-plantlix-900/20 rounded-xl p-3 sm:p-5 border-2 transition-all shadow-glow hover:shadow-lg cursor-pointer ${filterStatus === 'LEARNED'
                        ? 'border-plantlix-500 dark:border-plantlix-500 ring-2 ring-plantlix-300 dark:ring-plantlix-700'
                        : 'border-plantlix-200 dark:border-plantlix-800 hover:border-plantlix-400 dark:hover:border-plantlix-600'
                        }`}
                >
                    <div className="text-3xl font-bold text-plantlix-500 dark:text-plantlix-400 mb-1">{stats.learned}</div>
                    <div className="text-xs font-medium text-plantlix-500 dark:text-plantlix-400 uppercase tracking-wide">Gelernt</div>
                </div>
                <div
                    onClick={() => setFilterStatus(filterStatus === 'IN_PROGRESS' ? '' : 'IN_PROGRESS')}
                    className={`card card-elevated glass bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/30 dark:to-yellow-900/20 rounded-xl p-3 sm:p-5 border-2 transition-all cursor-pointer ${filterStatus === 'IN_PROGRESS'
                        ? 'border-yellow-500 dark:border-yellow-500 ring-2 ring-yellow-300 dark:ring-yellow-700'
                        : 'border-yellow-200 dark:border-yellow-800 hover:border-yellow-400 dark:hover:border-yellow-600'
                        }`}
                >
                    <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-1">{stats.inProgress}</div>
                    <div className="text-xs font-medium text-yellow-600 dark:text-yellow-400 uppercase tracking-wide">In Arbeit</div>
                </div>
                <div
                    onClick={() => setFilterStatus(filterStatus === 'NOT_LEARNED' ? '' : 'NOT_LEARNED')}
                    className={`card card-elevated glass bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-900/20 rounded-xl p-3 sm:p-5 border-2 transition-all cursor-pointer ${filterStatus === 'NOT_LEARNED'
                        ? 'border-red-500 dark:border-red-500 ring-2 ring-red-300 dark:ring-red-700'
                        : 'border-red-200 dark:border-red-800 hover:border-red-400 dark:hover:border-red-600'
                        }`}
                >
                    <div className="text-3xl font-bold text-red-600 dark:text-red-400 mb-1">{stats.notLearned}</div>
                    <div className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide">Nicht gelernt</div>
                </div>
            </div>

            {/* Filter & Suche */}
            <div className="card glass bg-white dark:bg-[#1a1a1a] rounded-xl p-3 sm:p-5 mb-6 border border-gray-200 dark:border-gray-800 space-y-4 shadow-md">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <IconList className="text-plantlix-500 dark:text-plantlix-400" />
                            Liste
                        </label>
                        <select
                            value={selectedPlantList}
                            onChange={(e) => {
                                setSelectedPlantList(e.target.value);
                                if (trackInteraction) {
                                    trackInteraction('PLANT_LIST_CHANGE', { list: e.target.value, context: 'bestiary' });
                                }
                            }}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-plantlix-500 focus:border-plantlix-500 transition-all"
                        >
                            <option value="GALABAUER">GaLaBau Sachsen {listStats ? `(${listStats['GALABAUER']})` : ''}</option>
                            <option value="FRIEDHOFSGAERTNER">
                                Friedhof Sachsen {listStats ? `(${listStats['FRIEDHOFSGAERTNER']})` : ''}
                            </option>
                            <option value="ZIERPFLANZENGAERTNER">
                                Zierpflanzen Sachsen {listStats ? `(${listStats['ZIERPFLANZENGAERTNER']})` : ''}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <IconSearch className="text-plantlix-500 dark:text-plantlix-400" />
                            Suche
                        </label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Botanischer Name, Deutscher Name, Familie..."
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-plantlix-500 focus:border-plantlix-500 transition-all"
                        />
                    </div>
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <IconFolder className="text-plantlix-500 dark:text-plantlix-400" />
                            Kategorie
                        </label>
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-plantlix-500 focus:border-plantlix-500 transition-all"
                        >
                            <option value="">Alle Kategorien</option>
                            {availableCategories.map(cat => (
                                <option key={cat.id} value={String(cat.id)}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <IconChart className="text-plantlix-500 dark:text-plantlix-400" />
                            Status
                        </label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-plantlix-500 focus:border-plantlix-500 transition-all"
                        >
                            <option value="">Alle Status</option>
                            <option value="LEARNED">Gelernt</option>
                            <option value="IN_PROGRESS">In Arbeit</option>
                            <option value="NOT_LEARNED">Nicht gelernt</option>
                        </select>
                    </div>
                </div>
                <div className="flex items-center justify-between flex-wrap gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            Sortieren nach:
                        </span>
                        {['botanical_name', 'german_name', 'family', 'status'].map(field => (
                            <button
                                key={field}
                                onClick={() => handleSort(field)}
                                className={`px-3 py-1 rounded-lg text-sm font-medium transition ${sortBy === field
                                    ? 'bg-plantlix-500 text-white'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                                    }`}
                            >
                                {field === 'botanical_name' ? 'Botanischer Name' :
                                    field === 'german_name' ? 'Deutscher Name' :
                                        field === 'family' ? 'Familie' : 'Status'}
                                {sortBy === field && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
                            </button>
                        ))}
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400 mr-1">
                            Größe:
                        </span>
                        <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                            <button 
                                onClick={() => setSizeLevel(prev => Math.max(1, prev - 1))} 
                                disabled={sizeLevel === 1}
                                className="w-8 h-8 flex items-center justify-center rounded-md text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                <span className="text-lg leading-none">-</span>
                            </button>
                            <span className="w-8 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                {sizeLevel}
                            </span>
                            <button 
                                onClick={() => setSizeLevel(prev => Math.min(3, prev + 1))} 
                                disabled={sizeLevel === 3}
                                className="w-8 h-8 flex items-center justify-center rounded-md text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                <span className="text-lg leading-none">+</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Vergleichs-Info */}
            {comparePlants.length > 0 && (
                <div className="card glass bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-xl p-5 mb-6 flex items-center justify-between shadow-glow animate-fadeIn">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">⚖️</span>
                        <div>
                            <div className="font-semibold text-blue-900 dark:text-blue-100">
                                {comparePlants.length} Pflanze{comparePlants.length > 1 ? 'n' : ''} zum Vergleich ausgewählt
                            </div>
                            <div className="text-sm text-blue-700 dark:text-blue-300">
                                {comparePlants.map(p => p.botanical_name).join(', ')}
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {comparePlants.length === 2 && (
                            <button
                                onClick={() => onCompareClick(comparePlants)}
                                className="px-4 py-2 font-medium bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                            >
                                Vergleichen →
                            </button>
                        )}
                        <button
                            onClick={() => onCompareClick([])}
                            className="px-4 py-2 font-medium bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#242424] rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-800"
                        >
                            Zurücksetzen
                        </button>
                    </div>
                </div>
            )}

            {/* Pflanzen-Grid */}
            <div className="flex-1 w-full pb-12 mt-6">
                {listLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="card bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden h-[420px] flex flex-col">
                                <div className="h-48 flex-shrink-0 bg-gray-200 dark:bg-gray-800 animate-pulse"></div>
                                <div className="p-4 flex flex-col flex-grow space-y-3">
                                    <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-3/4 animate-pulse"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/2 animate-pulse"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/3 animate-pulse"></div>
                                    <div className="flex gap-2 pt-2 mt-auto">
                                        <div className="h-10 bg-gray-200 dark:bg-gray-800 rounded flex-1 animate-pulse"></div>
                                        <div className="h-10 bg-gray-200 dark:bg-gray-800 rounded w-10 animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : sortedPlants.length === 0 ? (
                    <div className="text-center py-12 bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800">
                        <div className="text-6xl mb-4">🔍</div>
                        <p className="text-gray-600 dark:text-gray-400">
                            Keine Pflanzen gefunden. Versuche andere Filter.
                        </p>
                    </div>
                ) : (
                    <div 
                        className={`grid gap-4 ${
                            sizeLevel === 1 ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6' : 
                            sizeLevel === 2 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5' : 
                            'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                        }`}
                    >
                        {sortedPlants.map(plant => {
                            const plantData = userPlantData[plant.id] || { learning_status: 'NOT_LEARNED', is_favorite: false };
                            const status = plantData.learning_status;
                            const mainImage = plant.images && plant.images.length > 0 ? plant.images[0] : null;
                            const inCompare = isInCompare(plant.id);
                            
                            return (
                                <div
                                    key={plant.id}
                                    className={`card card-elevated group flex flex-col bg-white dark:bg-[#1a1a1a] rounded-xl border-2 transition-all cursor-pointer animate-fadeIn ${inCompare
                                        ? 'border-blue-500 shadow-glow ring-2 ring-blue-300 dark:ring-blue-700'
                                        : 'border-gray-200 dark:border-gray-800 hover:border-plantlix-400 dark:hover:border-plantlix-600'
                                        }`}
                                    onClick={() => onPlantClick(plant)}
                                >
                                    {/* Bild - explicitly optimized and loading lazy inside container */}
                                    <div className="relative flex-shrink-0 bg-gray-100 dark:bg-gray-800 rounded-t-lg overflow-hidden h-40 sm:h-48">
                                        {mainImage ? (
                                            <OptimizedImage
                                                src={mainImage.image_medium || mainImage.image_small}
                                                fallbackSrc={mainImage.image}
                                                alt={plant.botanical_name}
                                                className="w-full h-full object-cover"
                                                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-6xl text-gray-300 dark:text-gray-700">
                                                <LeafIcon className="w-14 h-14" />
                                            </div>
                                        )}
                                        {/* Status, Favorite, Compare Badges */}
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <LearningStatusBadge
                                                status={status}
                                                showLabel={false}
                                                size="sm"
                                                className="absolute top-2 right-2 shadow-lg"
                                                plantId={plant.id}
                                                onUpdateStatus={onUpdateUserPlantData}
                                            />
                                        </div>
                                        {/* Selection Checkbox */}
                                        <button
                                            onClick={(e) => toggleSelect(e, plant.id)}
                                            className={`absolute top-2 left-2 p-1 rounded-lg transition-all z-20 ${selectedIds.includes(plant.id) ? 'bg-plantlix-500 text-white shadow-lg scale-110' : 'bg-white/90 dark:bg-black/80 text-gray-400 opacity-20 group-hover:opacity-100 hover:text-plantlix-500 hover:scale-105'}`}
                                        >
                                            {selectedIds.includes(plant.id) ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                                        </button>
                                        
                                        {plantData.is_favorite && !selectedIds.includes(plant.id) && (
                                            <div className="absolute top-9 left-2 text-2xl">⭐</div>
                                        )}
                                        {inCompare && (
                                            <div className="absolute bottom-2 right-2 bg-blue-600 dark:bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg border border-blue-800 dark:border-blue-400">
                                                ⚖️ Ausgewählt
                                            </div>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="p-4 flex flex-col flex-grow text-left">
                                        <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-1 line-clamp-2" title={plant.botanical_name}>
                                            {plant.botanical_name}
                                        </h3>
                                        {plant.german_name && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-1" title={plant.german_name}>
                                                {plant.german_name}
                                            </p>
                                        )}
                                        {plant.family && (
                                            <p className="text-xs text-gray-500 dark:text-gray-500 mb-auto" title={plant.family}>
                                                {plant.family}
                                            </p>
                                        )}

                                        {/* Aktionen */}
                                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 z-10">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onPlantClick(plant);
                                                }}
                                                className="btn-primary flex-1 text-sm min-h-[36px] sm:min-h-[44px]"
                                            >
                                                Details
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (inCompare) {
                                                        onCompareClick(comparePlants.filter(p => p.id !== plant.id));
                                                    } else if (canAddToCompare) {
                                                        onCompareClick([...comparePlants, plant]);
                                                    } else {
                                                        alert('Du kannst maximal 2 Pflanzen vergleichen.');
                                                    }
                                                }}
                                                className={`w-11 h-11 flex items-center justify-center rounded-lg text-lg font-semibold transition-all ${inCompare
                                                    ? 'bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600 border border-red-700 dark:border-red-400 shadow-sm'
                                                    : canAddToCompare
                                                        ? 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 border border-blue-700 dark:border-blue-400 shadow-sm'
                                                        : 'bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-not-allowed border border-gray-400 dark:border-gray-600'
                                                    }`}
                                                disabled={!canAddToCompare && !inCompare}
                                                title={inCompare ? 'Aus Vergleich entfernen' : canAddToCompare ? 'Zum Vergleich hinzufügen' : 'Maximal 2 Pflanzen'}
                                            >
                                                {inCompare ? '✕' : '⚖️'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {sortedPlants.length === 0 && (
                <div className="text-center py-12 bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800">
                    <div className="text-6xl mb-4">🔍</div>
                    <p className="text-gray-600 dark:text-gray-400">
                        Keine Pflanzen gefunden. Versuche andere Filter.
                    </p>
                </div>
            )}

            <ExportModal 
                isOpen={isExportModalOpen} 
                onClose={() => setIsExportModalOpen(false)} 
                selectedPlantIds={exportMode === 'selected' ? selectedIds : sortedPlants.map(p => p.id)} 
                title={exportMode === 'selected' ? `Exportiere ${selectedIds.length} gewählte Pflanzen` : `Exportiere Liste mit Filtern (${sortedPlants.length})`}
            />

            {/* Sticky Bulk Selection Bar */}
            {selectedIds.length > 0 && (
                <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[2100] animate-in fade-in slide-in-from-bottom-4 duration-300 pointer-events-none">
                    <div className="bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl p-4 md:p-6 flex items-center gap-6 backdrop-blur-xl pointer-events-auto ring-1 ring-black/5 dark:ring-white/10">
                        <div className="flex flex-col">
                            <span className="text-sm font-extrabold text-gray-900 dark:text-gray-100">
                                {selectedIds.length} Pflanzen gewählt
                            </span>
                            <span className="text-[10px] uppercase tracking-wider font-bold text-plantlix-600 dark:text-plantlix-400">
                                Massen-Aktion
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    setExportMode('selected');
                                    setIsExportModalOpen(true);
                                }}
                                className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl font-bold border border-gray-200 dark:border-gray-700 transition-all shadow-sm"
                            >
                                <Download className="w-4 h-4" />
                                <span>Export</span>
                            </button>
                            <button
                                onClick={handleBulkCreateJournalEntries}
                                disabled={isBulking}
                                className="flex items-center gap-2 px-6 py-3 bg-plantlix-600 hover:bg-plantlix-700 disabled:opacity-50 text-white rounded-xl font-bold shadow-lg shadow-plantlix-600/20 transition-all active:scale-95"
                            >
                                {isBulking ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <BookOpen className="w-5 h-5" />
                                )}
                                <span>Im Journal erstellen</span>
                            </button>
                            <button
                                onClick={() => setSelectedIds([])}
                                className="ml-2 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                                title="Auswahl aufheben"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const CompareModal = ({ plants, userPlantData, onClose }) => {
    if (!plants || plants.length !== 2) return null;

    const [plant1, plant2] = plants;
    const data1 = userPlantData[plant1.id] || {};
    const data2 = userPlantData[plant2.id] || {};

    const compareFields = [
        { label: 'Botanischer Name', field: 'botanical_name' },
        { label: 'Deutscher Name', field: 'german_name' },
        { label: 'Familie', field: 'family' },
        { label: 'Kategorie', field: 'category_name' },
        { label: 'Zwischenprüfung', field: 'is_intermediate_exam_subject', type: 'boolean' },
        { label: 'Lernstatus', field: 'learning_status', type: 'user_data' },
        { label: 'Favorit', field: 'is_favorite', type: 'user_data_boolean' },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-800" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 p-4 flex items-center justify-between z-10">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <span className="text-3xl">⚖️</span>
                        <span>Pflanzen-Vergleich</span>
                    </h2>
                    <button
                        onClick={onClose}
                        className="w-12 h-12 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-2xl transition rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                        ×
                    </button>
                </div>

                {/* Vergleichs-Content */}
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Pflanze 1 */}
                        <div className="space-y-4">
                            <div className="text-center">
                                <div className="relative h-64 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden mb-4">
                                    {plant1.images && plant1.images.length > 0 ? (
                                        <OptimizedImage
                                            src={plant1.images[0].image}
                                            alt={plant1.botanical_name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-700">
                                            <LeafIcon className="w-16 h-16" />
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                    {plant1.botanical_name}
                                </h3>
                                {plant1.german_name && (
                                    <p className="text-gray-600 dark:text-gray-400">{plant1.german_name}</p>
                                )}
                            </div>

                            <div className="space-y-3">
                                {compareFields.map(field => {
                                    let value1;
                                    if (field.type === 'user_data') {
                                        value1 = getLearningStatusLabel(data1[field.field] || 'NOT_LEARNED');
                                    } else if (field.type === 'user_data_boolean') {
                                        value1 = data1[field.field] ? '⭐ Ja' : 'Nein';
                                    } else if (field.type === 'boolean') {
                                        value1 = plant1[field.field] ? '✔ Ja' : '✗ Nein';
                                    } else {
                                        value1 = plant1[field.field] || '-';
                                    }

                                    const value2 = field.type === 'user_data'
                                        ? getLearningStatusLabel(data2[field.field] || 'NOT_LEARNED')
                                        : field.type === 'user_data_boolean'
                                            ? data2[field.field] ? '⭐ Ja' : 'Nein'
                                            : field.type === 'boolean'
                                                ? plant2[field.field] ? '✔ Ja' : '✗ Nein'
                                                : plant2[field.field] || '-';

                                    const isEqual = value1 === value2;

                                    return (
                                        <div key={field.field} className="border border-gray-200 dark:border-gray-800 rounded-lg p-3">
                                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                                {field.label}
                                            </div>
                                            <div className={`text-sm font-medium ${isEqual ? 'text-gray-900 dark:text-gray-100' : 'text-blue-600 dark:text-blue-400'}`}>
                                                {value1}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Pflanze 2 */}
                        <div className="space-y-4">
                            <div className="text-center">
                                <div className="relative h-64 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden mb-4">
                                    {plant2.images && plant2.images.length > 0 ? (
                                        <OptimizedImage
                                            src={plant2.images[0].image}
                                            alt={plant2.botanical_name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-700">
                                            <LeafIcon className="w-16 h-16" />
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                    {plant2.botanical_name}
                                </h3>
                                {plant2.german_name && (
                                    <p className="text-gray-600 dark:text-gray-400">{plant2.german_name}</p>
                                )}
                            </div>

                            <div className="space-y-3">
                                {compareFields.map(field => {
                                    let value2;
                                    if (field.type === 'user_data') {
                                        value2 = getLearningStatusLabel(data2[field.field] || 'NOT_LEARNED');
                                    } else if (field.type === 'user_data_boolean') {
                                        value2 = data2[field.field] ? '⭐ Ja' : 'Nein';
                                    } else if (field.type === 'boolean') {
                                        value2 = plant2[field.field] ? '✔ Ja' : '✗ Nein';
                                    } else {
                                        value2 = plant2[field.field] || '-';
                                    }

                                    const value1 = field.type === 'user_data'
                                        ? getLearningStatusLabel(data1[field.field] || 'NOT_LEARNED')
                                        : field.type === 'user_data_boolean'
                                            ? data1[field.field] ? '⭐ Ja' : 'Nein'
                                            : field.type === 'boolean'
                                                ? plant1[field.field] ? '✔ Ja' : '✗ Nein'
                                                : plant1[field.field] || '-';

                                    const isEqual = value1 === value2;

                                    return (
                                        <div key={field.field} className="border border-gray-200 dark:border-gray-800 rounded-lg p-3">
                                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                                {field.label}
                                            </div>
                                            <div className={`text-sm font-medium ${isEqual ? 'text-gray-900 dark:text-gray-100' : 'text-blue-600 dark:text-blue-400'}`}>
                                                {value2}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Unterschiede-Highlight */}
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                            💡 Unterschiede werden blau hervorgehoben
                        </h3>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                            Gleiche Werte erscheinen in normaler Farbe, unterschiedliche Werte werden blau dargestellt.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Bestiary;
